import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import { Badge } from "@shared/ui/badge";
import { MapPin, Phone, Mail, Building, Users, Settings } from "lucide-react";

interface ServiceCenter {
  id: string;
  name: string;
  address: string;
  city: string;
  phone: string;
  email: string;
  areas: string[];
  subAreas: string[];
  premises: string[];
  services: string[];
  isActive: boolean;
  type: string;
}

interface ServiceCenterDetailProps {
  center: ServiceCenter | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ServiceCenterDetailView = ({ center, isOpen, onClose }: ServiceCenterDetailProps) => {
  if (!center) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Service Center Details
          </DialogTitle>
          <DialogDescription>
            View complete information for {center.name}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                <Building className="h-4 w-4" />
                Name
              </Label>
              <p className="text-sm text-muted-foreground mt-1">{center.name}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Type</Label>
              <div className="mt-1">
                <Badge variant="secondary">{center.type}</Badge>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Location Information
            </Label>
            <div className="p-4 border rounded-lg space-y-3">
              <div>
                <Label className="text-sm font-medium">Address</Label>
                <p className="text-sm text-muted-foreground">{center.address}, {center.city}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    Phone
                  </Label>
                  <p className="text-sm text-muted-foreground">{center.phone}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    Email
                  </Label>
                  <p className="text-sm text-muted-foreground">{center.email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Coverage Areas */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Users className="h-4 w-4" />
              Coverage Areas
            </Label>
            <div className="p-4 border rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">
                    Areas ({center.areas.length})
                  </Label>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {center.areas.length > 0 ? (
                      center.areas.map((area, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {area}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-xs text-muted-foreground">No areas assigned</p>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">
                    Sub Areas ({center.subAreas.length})
                  </Label>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {center.subAreas.length > 0 ? (
                      center.subAreas.map((subArea, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {subArea}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-xs text-muted-foreground">No sub areas assigned</p>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">
                  Premises ({center.premises.length})
                </Label>
                <div className="flex flex-wrap gap-1 mt-2">
                  {center.premises.length > 0 ? (
                    center.premises.map((premise, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {premise}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground">No premises assigned</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Available Services
            </Label>
            <div className="p-4 border rounded-lg">
              <div className="flex flex-wrap gap-2">
                {center.services.length > 0 ? (
                  center.services.map((service, index) => (
                    <Badge key={index} variant="default" className="text-xs">
                      {service}
                    </Badge>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No services configured</p>
                )}
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Status Information</Label>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Current Status</Label>
                <Badge variant={center.isActive ? "default" : "secondary"}>
                  {center.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {center.isActive 
                  ? "This service center is currently active and accepting customers." 
                  : "This service center is currently inactive and not accepting customers."}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};